import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { and, eq, gt, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import Button from 'tio-ui/components/button';
import ButtonOptions from './button-options.gts';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import type { EligibilityFileJobLogExtension as EligibilityFileJobLogModel } from 'tio-data/data/resources/eligibility-file-job-log.ext';
import type { TOC } from '@ember/component/template-only';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import Chip from 'tio-ui/components/chip';
import { HStack, VStack, Section } from 'tio-ui/components/layout';
import { PhArrowRight } from 'ember-phosphor-icons';

type StatTileIntent = 'success' | 'warning' | 'danger' | 'brand';

/**
 * One of the four processing-count tiles in the eligibility card
 * (created / updated / deactivated / reactivated). Owns the tinted
 * surface + colored value/label/percent; the caller yields the per-tile
 * action buttons. Off-brand emerald/amber re-home onto success/warning
 * status tokens (byte-identical light); red/purple route through the TIO
 * palette via the `.app-count-tile--*` recipe.
 */
const StatTile: TOC<{
  Args: {
    intent: StatTileIntent;
    value: string;
    label: string;
    percent?: string;
  };
  Blocks: { default: [] };
}> = <template>
  {{#let
    (if (eq @intent "success") "success")
    (if (eq @intent "warning") "warning")
    (if (eq @intent "danger") "danger")
    (if (eq @intent "brand") "brand")
    as |s w d b|
  }}
    {{#let (or s w d b) as |intent|}}
      <div class="app-count-tile app-count-tile--{{intent}} text-center p-2">
        <div class="font-semibold app-text-{{intent}}">
          {{@value}}
        </div>
        <div class="text-xs app-status-text-{{intent}}">{{@label}}</div>
        {{yield}}
        {{#if @percent}}
          <div class="text-xs mt-1 app-text-{{intent}} app-count-tile__pct">
            {{@percent}}
            {{t "eligibility_files.of_total"}}
          </div>
        {{/if}}
      </div>
    {{/let}}
  {{/let}}
</template>;

interface EligibilityFilesEligibilityCardSignature {
  Args: {
    item: EligibilityFileJobLogModel;
    showRoute: string;
    indexRoute: string;
    abortProcess?: (item: EligibilityFileJobLogModel) => void;
    beginProcess?: (item: EligibilityFileJobLogModel) => void;
    userId?: string;
    onViewDetails?: (item: EligibilityFileJobLogModel, category: string) => void;
    onViewTopChanges?: (item: EligibilityFileJobLogModel) => void;
  };
}

export default class EligibilityFilesEligibilityCard extends Component<EligibilityFilesEligibilityCardSignature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  get isAdmin() {
    // TODO: There are other services to figure this out. If routes are moved around this would break.
    return !!this.router.currentRouteName?.includes('authenticated.eligibility.index');
  }

  get isAborted() {
    return this.args.item.hasAborted;
  }

  get processTotal() {
    return this.args.item.isLoading ? 'Loading' : this.args.item.totalProcessed;
  }

  get unchangedTotal() {
    return this.args.item.isLoading ? 'Loading' : this.args.item.totalUnchanged;
  }

  get processCount() {
    return this.args.item.isLoading ? undefined : this.args.item.counts;
  }

  get previewCount() {
    return this.args.item.isLoading ? undefined : this.args.item.previewCounts;
  }

  get autoProcessEnabled() {
    return this.args.item.autoProcessEnabled;
  }

  countPercentage = (previewCount: number = 0, processCount: number = 0) => {
    if (previewCount === 0) {
      return 0;
    }
    return Math.floor((processCount / previewCount) * 100).toString() + '%';
  };

  get totalRecords() {
    const p = this.previewCount;
    if (!p) return 0;
    return p.unchanged + p.updated + p.deactivated;
  }

  percentOfTotal = (categoryCount: number = 0) => {
    if (this.totalRecords === 0 || categoryCount === 0) return '';
    const pct = (categoryCount / this.totalRecords) * 100;
    return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
  };

  showAbortStatus = (status: string) => {
    return status.toLowerCase().replace(/_/g, ' ');
  };

  convertToReadableName = (status: string) => {
    return this.intl.t('common.eligibility_file_upload.status.' + status) || 'ALL';
  };

  get statusIntent() {
    const status = this.args.item.status?.toLowerCase();
    if (status?.includes('completed')) return 'success';
    if (status?.includes('processing') || status?.includes('running')) return 'warning';
    if (status?.includes('failed') || status?.includes('error') || status?.includes('abort'))
      return 'danger';
    return 'default';
  }

  viewDetails = (item: EligibilityFileJobLogModel, category: string) => {
    this.args.onViewDetails?.(item, category);
  };

  get strategyDisplay() {
    const strategy = this.args.item.strategy;
    if (eq(strategy, 'MANUAL')) return this.intl.t('eligibility_files.manual');
    if (eq(strategy, 'MANUAL_FORCE_UPDATE')) return this.intl.t('eligibility_files.forced');
    if (eq(strategy, 'MANUAL_EXTERNAL_SERVICE')) return this.intl.t('eligibility_files.external');
    return this.intl.t('eligibility_files.automatic');
  }

  <template>
    <Section
      class="app-eligibility-card border tio-border-default rounded-lg shadow-sm hover:shadow-md transition-shadow"
    >
      <:header>
        <div>
          {{#unless this.autoProcessEnabled}}
            <div class="text-sm tio-fg-danger tio-bg-danger-soft p-2 rounded mb-2">
              {{t "eligibility_files.auto_process_disabled"}}
            </div>
          {{/unless}}
          <LinkTo
            @route={{@showRoute}}
            @model={{@item.id}}
            class="app-text-link app-text-link--underline-on-hover tio-text-heading"
            title={{@item.fileName}}
          >
            {{@item.fileName}}
            <PhArrowRight @size={{16}} class="inline-block ml-1" />
          </LinkTo>
        </div>
      </:header>

      <:body>
        <VStack>
          <div class="tio-row-start-between">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm flex-1">
              {{#if this.isAdmin}}
                <div>
                  <span class="font-medium tio-fg-secondary">{{t
                      "eligibility_files.partner_label"
                    }}</span>
                  <span class="ml-2 tio-fg-primary">{{@item.company.name}}</span>
                </div>
              {{/if}}
              <div>
                <span class="font-medium tio-fg-secondary">{{t
                    "eligibility_files.strategy_label"
                  }}</span>
                <span class="ml-2 tio-fg-primary">{{this.strategyDisplay}}</span>
              </div>
              <div>
                <span class="font-medium tio-fg-secondary">{{t
                    "eligibility_files.started_label"
                  }}</span>
                <span class="ml-2 tio-fg-primary">
                  {{safeFormatDate @item.startTime}},
                  {{safeFormatTime @item.startTime}}
                </span>
              </div>
              {{#if @item.endTime}}
                <div>
                  <span class="font-medium tio-fg-secondary">{{t
                      "eligibility_files.completed_label"
                    }}</span>
                  <span class="ml-2 tio-fg-primary">
                    {{safeFormatDate @item.endTime}},
                    {{safeFormatTime @item.endTime}}
                  </span>
                </div>
              {{/if}}
            </div>
            <div class="shrink-0">
              <Chip @intent={{this.statusIntent}}>
                {{#if this.isAborted}}
                  {{this.showAbortStatus @item.status}}
                {{else}}
                  {{this.convertToReadableName @item.status}}
                {{/if}}
              </Chip>
            </div>
          </div>

          {{#if this.isAdmin}}
            <div class="rounded-lg p-3">
              <div class="tio-text-sm-medium tio-fg-secondary mb-2">{{t
                  "eligibility_files.processing_summary"
                }}</div>
              <HStack>
                <div>
                  <span class="font-semibold tio-fg-success">{{this.processTotal}}</span>
                  <span class="tio-fg-secondary ml-1">{{t
                      "eligibility_files.completed_count"
                    }}</span>
                </div>
                <div>
                  <span class="font-semibold tio-fg-secondary">{{this.unchangedTotal}}</span>
                  <span class="tio-fg-secondary ml-1">{{t
                      "eligibility_files.unchanged_count"
                    }}</span>
                </div>
              </HStack>
            </div>

            {{#if (and this.processCount this.previewCount)}}
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <StatTile
                  @intent="success"
                  @value="{{this.processCount.created}}/{{this.previewCount.created}}"
                  @label={{t "eligibility_files.created_label"}}
                  @percent={{this.percentOfTotal this.previewCount.created}}
                >
                  {{#if (and @onViewDetails (gt this.previewCount.created 0))}}
                    <Button
                      @appearance="minimal"
                      @size="sm"
                      class="text-xs hover:underline mt-1"
                      {{on "click" (fn this.viewDetails @item "created")}}
                    >
                      {{t "eligibility_files.view_details"}}
                    </Button>
                  {{/if}}
                </StatTile>

                <StatTile
                  @intent="warning"
                  @value="{{this.processCount.updated}}/{{this.previewCount.updated}}"
                  @label={{t "eligibility_files.updated_label"}}
                  @percent={{this.percentOfTotal this.previewCount.updated}}
                >
                  {{#if (gt this.previewCount.updated 0)}}
                    <div class="tio-row-center gap-1 mt-1">
                      {{#if @onViewDetails}}
                        <Button
                          @appearance="minimal"
                          @size="sm"
                          class="text-xs hover:underline"
                          {{on "click" (fn this.viewDetails @item "updated")}}
                        >
                          {{t "eligibility_files.view_details"}}
                        </Button>
                      {{/if}}
                      {{#if (and @onViewDetails @onViewTopChanges)}}
                        <span class="tio-fg-faint text-xs">|</span>
                      {{/if}}
                      {{#if @onViewTopChanges}}
                        <Button
                          @appearance="minimal"
                          @size="sm"
                          class="text-xs hover:underline"
                          {{on "click" (fn @onViewTopChanges @item)}}
                        >
                          {{t "eligibility_files.top_changes"}}
                        </Button>
                      {{/if}}
                    </div>
                  {{/if}}
                </StatTile>

                <StatTile
                  @intent="danger"
                  @value="{{this.processCount.deactivated}}/{{this.previewCount.deactivated}}"
                  @label={{t "eligibility_files.deactivated_label"}}
                  @percent={{this.percentOfTotal this.previewCount.deactivated}}
                >
                  {{#if (and @onViewDetails (gt this.previewCount.deactivated 0))}}
                    <Button
                      @appearance="minimal"
                      @size="sm"
                      class="text-xs hover:underline mt-1"
                      {{on "click" (fn this.viewDetails @item "deactivated")}}
                    >
                      {{t "eligibility_files.view_details"}}
                    </Button>
                  {{/if}}
                </StatTile>

                <StatTile
                  @intent="brand"
                  @value="{{this.processCount.reactivated}}/{{this.previewCount.reactivated}}"
                  @label={{t "eligibility_files.reactivated_label"}}
                  @percent={{this.percentOfTotal this.previewCount.reactivated}}
                >
                  {{#if (and @onViewDetails (gt this.previewCount.reactivated 0))}}
                    <Button
                      @appearance="minimal"
                      @size="sm"
                      class="text-xs hover:underline mt-1"
                      {{on "click" (fn this.viewDetails @item "reactivated")}}
                    >
                      {{t "eligibility_files.view_details"}}
                    </Button>
                  {{/if}}
                </StatTile>
              </div>
            {{/if}}
          {{/if}}
        </VStack>
      </:body>

      <:actions>
        {{#if this.isAdmin}}
          <ButtonOptions
            @indexRoute={{@indexRoute}}
            @status={{@item.status}}
            @companyId={{or @item.company.id ""}}
            @legacyId={{@item.company.legacyId}}
            @fileName={{@item.fileName}}
            @abortProcess={{@abortProcess}}
            @beginProcess={{@beginProcess}}
            @item={{@item}}
            @currentUserId={{or @userId ""}}
          />
        {{/if}}
      </:actions>
    </Section>
  </template>
}
